<script setup>
import { ref, computed } from 'vue'
import { PhList, PhX, PhGear, PhUser, PhSignOut, PhBell } from '@phosphor-icons/vue'
import UserMale from '@/../images/user-male.png'
import { Link, router, usePage } from '@inertiajs/vue3'
import { flashToast } from "@/toast.js"
import { useLoader } from '@/Stores/useLoader'
import { capitalize } from '@/helpers.js';

defineProps({ sidebarOpen: Boolean })
const emit = defineEmits(['toggleSidebar'])

const active = ref(false)
const toggleUserDropdown = () => { active.value = !active.value }
const closeDropdown = () => { active.value = false }

const page = usePage()
const user = computed(() => page.props?.auth?.user || {})

const loader = useLoader()
const logout = () => {
  loader.start()
  router.post('/logout', {}, {
    onSuccess: (page) => { flashToast(page.props.flash) },
    onFinish:  () => { loader.finish() }
  })
}
</script>

<template>
  <header
    class="fixed top-0 right-0 z-30 h-16 bg-nav border-b border-nav-border shadow-sm transition-all w-full md:w-[calc(100%-250px)]"
  >
    <div class="flex items-center justify-between h-full px-5">

      <!-- Mobile Toggle -->
      <button
        @click="emit('toggleSidebar')"
        class="md:hidden text-gray-500 hover:text-gray-700 transition"
      >
        <PhX v-if="sidebarOpen" :size="22" />
        <PhList v-else :size="22" />
      </button>

      <div class="flex-1"></div>

      <!-- Right Section -->
      <div class="flex items-center gap-2">

        <!-- Bell -->
        <button class="relative w-9 h-9 flex items-center justify-center rounded-lg
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
          <PhBell :size="20" />
          <span class="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
        </button>

        <div class="w-px h-6 bg-gray-200 mx-1"></div>

        <!-- User -->
        <div class="relative">
          <button
            @click="toggleUserDropdown"
            class="flex items-center gap-2.5 pl-1 pr-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <img class="w-8 h-8 rounded-full object-cover ring-2 ring-gray-200" :src="UserMale" />
            <div class="hidden sm:flex flex-col items-start leading-tight">
              <span class="text-sm font-semibold text-gray-800">{{ user?.name ?? 'Admin' }}</span>
              <span class="text-xs text-gray-400">{{ capitalize(user?.role?.name ?? 'Administrator') }}</span>
            </div>
          </button>

          <div v-if="active" class="fixed inset-0 z-20" @click="closeDropdown"></div>

          <Transition
            enter-active-class="transition ease-out duration-150"
            enter-from-class="opacity-0 -translate-y-1 scale-95"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 -translate-y-1 scale-95"
          >
            <div
              v-if="active"
              class="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg
                     border border-gray-100 z-30 overflow-hidden py-1"
            >
              <div class="px-4 py-3 border-b border-gray-100">
                <p class="text-sm font-semibold text-gray-800">{{ user?.name ?? 'Admin' }}</p>
                <p class="text-xs text-gray-400 mt-0.5">{{ user?.email ?? '' }}</p>
              </div>

              <div class="py-1">
                <Link
                  href="/admin/profile"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600
                         hover:bg-gray-50 hover:text-gray-900 transition"
                >
                  <PhUser :size="15" class="text-gray-400" /> Profile
                </Link>
                <a class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600
                          hover:bg-gray-50 hover:text-gray-900 transition cursor-pointer">
                  <PhGear :size="15" class="text-gray-400" /> Settings
                </a>
              </div>

              <div class="border-t border-gray-100 py-1">
                <button
                  @click="logout"
                  :disabled="loader.loading"
                  class="flex items-center gap-3 w-full px-4 py-2.5 text-sm
                         text-red-500 hover:bg-red-50 transition disabled:opacity-60"
                >
                  <PhSignOut :size="15" /> Logout
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </header>
</template>